import {mapGetters, mapState} from 'vuex';
import Vue from 'vue';
import moment from 'moment-timezone';
import CONFIG from '@/constants/config.js';
import {dateMixin} from '@/utils/date-mixin';
import {solutionMixin} from '@/utils/solution-mixin';
import {countdownWindow, hasCountdown, hasDateValue, remainingEventMillis, remainingEventMinutes} from '@/utils/event-time.mjs';
import {buildFooterMenu, hasMainEntry} from '@/utils/footer-menu.mjs';
import Utils from "../store/modules/base";

const EMPTY_FOOTER_MENU = Object.freeze({ main: [], overflow: [], actions: [] });

// build features of the target in menu order; a feature exists when its url is set
const FOOTER_FEATURES = [
    ['data', CONFIG.FEATURE_IFRAME],
    ['gallery', CONFIG.FEATURE_GALLERY],
    ['evidence', CONFIG.FEATURE_EVIDENCE],
    ['draw', CONFIG.FEATURE_DRAWING],
];

export var eventMixin = {
    mixins: [dateMixin, solutionMixin],
    watch: {
        '$route.params': {
            handler: function(params) {
                if (params) {
                    this.urlEventId = params.id;
                }
            },
            deep: true,
            immediate: true
        },
        event_loading: {
            handler() {
                this.setEventLoadingDelayed();
            },
            immediate: true
        }
    },

    data() {
        return {
            urlEventId: null,
            event_loading_delayed: false,
            event_loading_delayed_pointer: null,
            checkinError: false
        };
    },
    computed: {
        ...mapGetters('events', [
            'firstEvent',
            'firstXEvents',
            'allEvents',
            'selectedEventId',
            'selectedEvent',
            'hasSelectedEvent',
            'getSelectedEventFooterLayout',
            'mapTaskListDisabled'
        ]),
        ...mapState('events', [
            'event_loading',
            'event_single_loading',
            'event_timezone',
            'event_error',
            'app_user_statistic',
            'app_team_photo',
            'app_team_name'
        ]),

        isLoadingEvents() {
            return this.event_loading_delayed;
        },
        isLoadingEventsImmediately() {
            return this.event_loading;
        },
        // the team countdown has run out; the server flag active does not know the countdown
        countdownExpired() {
            const event = this.selectedEvent;
            if (!hasCountdown(event) || !hasDateValue(event.first_task_checkout_time)) {
                return false;
            }
            return remainingEventMillis(event, Date.now(), (naive) => this.parseInEventTimezone(naive, event.timezone)) === 0;
        },
        eventIsInactive() {
            return this.selectedEvent && (!this.selectedEvent.active || this.countdownExpired);
        },
        timezone() {
            return this.selectedEvent ? this.selectedEvent.timezone : null;
        },
        serverSync() {
            return this.selectedEvent ? this.selectedEvent.force_server_sync : false;
        },
        teamName() {
            return this.selectedEvent ? this.selectedEvent.team_name : this.$t('app_teamname_default');
        },
        teamId() {
            return this.selectedEvent ? this.selectedEvent.team_id : null;
        },
        /**
         * Dates for the header countdown: the fixed event window, or the team countdown from
         * the N-th task checkout; frozenMs holds the full countdown before that checkout.
         */
        headerCountdown() {
            const event = this.selectedEvent;
            const timezone = event ? event.timezone : null;
            const range = countdownWindow(event, (naive) => this.parseInEventTimezone(naive, timezone));
            const toDate = (ms) => (ms === null ? null : (timezone ? moment.tz(ms, timezone) : moment(ms)).format());
            return { startDate: toDate(range.startMs), endDate: toDate(range.endMs), frozenMs: range.frozenMs };
        },
        eventIsInLiveMode() {
            return this.selectedEvent ? this.selectedEvent.status_id == CONFIG.STATUS_LIVE : false;
        },
        eventIsInTestMode() {
            return this.selectedEvent
                ? this.selectedEvent.status_id == CONFIG.STATUS_CREATING || this.selectedEvent.status_id == CONFIG.STATUS_TESTING
                : false;
        },
        eventIsInFinishedMode() {
            return this.selectedEvent ? this.selectedEvent.status_id == CONFIG.STATUS_FINISHED : false;
        },
        eventModeColor() {
            if (this.eventIsInLiveMode) {
                return 'red';
            } else if (this.eventIsInFinishedMode) {
                return 'red';
            } else {
                return 'grey darken-2';
            }
        },
        settingsShowIntNr() {
            return this.selectedEvent ? this.selectedEvent.show_int_nr : true;
        },
        settingsShowHighscore() {
            return this.selectedEvent ? this.selectedEvent.show_points : true;
        },
        settingsShowMapTiles() {
            return ( this.selectedEvent && Object.prototype.hasOwnProperty.call(this.selectedEvent, 'disable_map') ) ? !this.selectedEvent.disable_map : false;
        },
        settingsMapTypeDefault() {
            // map_type_default event setting (0 = roadmap, 1 = satellite); missing or invalid values fall back to 0
            const mapType = this.selectedEvent ? parseInt(this.selectedEvent.map_type_default, 10) : NaN;
            return isNaN(mapType) ? 0 : mapType;
        },
        // no tracking before an event is loaded; a loaded event without the field counts as GPS enabled
        settingsGpsDisabled() {
            if (this._.isEmpty(this.selectedEvent)) {
                return true;
            }
            return this.selectedEvent.disable_gps !== undefined ? this.selectedEvent.disable_gps : false;
        },
        settingsEventNeedWebsocketConnection() {
            //TODO: need other conditions to start websocket?
            return this.selectedEvent ? this.selectedEvent.master_slave || this.selectedEvent.showPlayers : false;
        },

        settingsUseMasterSlave() {
            return this.selectedEvent ? this.selectedEvent.master_slave : false;
        },

        customLogo() {
            return this.selectedEvent && !this._.isEmpty(this.selectedEvent.bg_terminal_logo)
                ? this.getBackgroundImageUrl(this.selectedEvent.bg_terminal_logo)
                : null;
        },
        customBackground() {
            return this.selectedEvent && !this._.isEmpty(this.selectedEvent.bg_terminal)
                ? this.getBackgroundImageUrl(this.selectedEvent.bg_terminal)
                : null;
        },
        isQuizEvent() {
            return this.selectedEvent ? this.selectedEvent.gametype_id == CONFIG.GAMETYPE_QUIZ : false;
        },
        hasIndoorMaps() {
            return this.selectedEvent ? !this._.isEmpty(this.selectedEvent.indoormaps) : false;
        },
        hasFinishText() {
            return !this._.isEmpty(this.finishText);
        },
        finishText() {
            return Utils.replaceKeywords(this.selectedEvent ? this.selectedEvent.finish_text : null, this.getOfflineStatistic, this.allSpots, this.keywordExtras)
        },
        /**
         * Data for the keywords beyond the offline statistic (show_event_time,
         * show_team_photo, show_task_solution media URLs). Reactive on event reload and
         * team photo upload; the remaining time is a snapshot of the render moment.
         */
        keywordExtras() {
            const event = this.selectedEvent;
            const timezone = event && event.timezone ? event.timezone : moment.tz.guess();
            const photo = this.app_team_photo;
            return {
                remainingEventMinutes: remainingEventMinutes(event, Date.now(), (naive) => moment.tz(naive, timezone).valueOf()),
                timeUnitLabel: (unit, count) => this.$tc(unit, count),
                teamPhotoUrl: photo && photo.url ? `${photo.baseUrl || ''}${photo.url}` : null,
                mediaBaseUrl: CONFIG.IMAGE_BASE_URL + '/',
                eventId: this.selectedEventId,
                teamId: this.teamId,
            };
        },
        imageBaseUrl() {
            return CONFIG.IMAGE_BASE_URL + '/';
        },
        labelTasks() {
            return this.selectedEvent && this.selectedEvent.lbl_app_task ? this.selectedEvent.lbl_app_task : this.$t('menuTaskList');
        },
        labelHighscore() {
            return this.selectedEvent && this.selectedEvent.lbl_app_highscore
                ? this.selectedEvent.lbl_app_highscore
                : this.$t('menuHighScore');
        },
        labelInfo() {
            return this.selectedEvent && this.selectedEvent.lbl_app_info ? this.selectedEvent.lbl_app_info : this.$t('menuInformation');
        },
        labelMap() {
            return this.selectedEvent && this.selectedEvent.lbl_app_map ? this.selectedEvent.lbl_app_map : this.$t('menuMap');
        },
        labelTask() {
            return this.selectedEvent && this.selectedEvent.custom_task_name ? this.selectedEvent.custom_task_name : this.$t('menuTask');
        },
        labelTaskAac() {
            return this.selectedEvent && this.selectedEvent.lbl_app_task_aac ? this.selectedEvent.lbl_app_task_aac : this.$t('taskFeedback');
        },
        labelTaskCheckin() {
            return this.selectedEvent && this.selectedEvent.lbl_app_task_checkin
                ? this.selectedEvent.lbl_app_task_checkin
                : this.$t('taskActive', { task: this.labelTask });
        },
        labelTaskPass() {
            return this.selectedEvent && this.selectedEvent.lbl_app_task_pass
                ? this.selectedEvent.lbl_app_task_pass
                : this.$t('taskCheckin');
        },
        labelTaskSolve() {
            //if voiting
            if (this.isSolutionTypeVoting(this.selectedSpot)) {
                return this.$t('taskSolveVoting');
            }
            return this.selectedEvent && this.selectedEvent.lbl_app_task_solve
                ? this.selectedEvent.lbl_app_task_solve
                : this.$t('taskSolve', { task: this.labelTask });
        },
        labelPoints() {
            return this.selectedEvent && this.selectedEvent.custom_points_name
                ? this.selectedEvent.custom_points_name
                : this.$t('taskPoints');
        },
        // the map belongs to the game when the main menu has a map entry (layout or default)
        shouldShowMap(){
            return hasMainEntry(this.footerMenu, 'map');
        },
        /**
         * Footer menu of the shell from the event layout: main bar cells (null = empty cell),
         * overflow entries and checkin actions. Empty until the event is checked in, so no link
         * points to a missing event id.
         */
        footerMenu(){
            const layout = this.getSelectedEventFooterLayout;
            if (!layout || !this.selectedEventId) {
                return EMPTY_FOOTER_MENU;
            }
            const customPageTitle = this.$store.getters['events/getCustomPageTitle'];
            return buildFooterMenu({
                layout,
                isQuiz: this.isQuizEvent,
                isMobile: this.$vuetify.breakpoint.smAndDown,
                eventId: this.selectedEventId,
                labels: {
                    map: this.labelMap,
                    tasks: this.labelTasks,
                    info: this.labelInfo,
                    highscore: this.labelHighscore,
                    qr: this.$t('menuQR'),
                    password: this.$t('menuPassword'),
                },
                customPageTitles: { 1: customPageTitle(1), 2: customPageTitle(2), 3: customPageTitle(3) },
                features: FOOTER_FEATURES
                    .filter(([, feature]) => feature && feature.url)
                    .map(([key, feature]) => ({ key, title: this.$t(feature.title), icon: `mdi-${feature.icon}` })),
                hasQrCheckin: this.$store.getters['spots/hasQrCheckin'],
                hasPwCheckin: this.$store.getters['spots/hasPwCheckin'],
            });
        },
    },
    methods: {
        setThemeColors() {
            const selectedEvent = this.selectedEvent
            if(typeof selectedEvent !== "undefined" && selectedEvent != null) {
                // localStorage.setItem('selectedEvent', JSON.stringify(selectedEvent));
                if(Object.prototype.hasOwnProperty.call(selectedEvent, 'color_theme')) {
                    // console.log("setThemeColors", selectedEvent);
                    this.setThemeColorValues(selectedEvent.color_theme);
                }
            } else {
                this.setThemeDefaultColorValues();
            }
        },
        setThemeColorValues(selectedEventColors) {
            // console.log('setThemeColorValues', selectedEventColors);
            // console.log('setThemeColorValues_BEFORE', this.$vuetify.theme.themes.light);
            this.setThemeDefaultColorValues()
            if (!selectedEventColors) {
                return
            }
            const colorArray = JSON.parse(selectedEventColors);
            colorArray.forEach(element => {
                if (element.name === 'custom_theme_background_color' && element.value !== null) {
                    this.$vuetify.theme.themes.light.backgroundColor = element.value;
                } else if (element.name === 'custom_theme_text_color' && element.value !== null) {
                    this.$vuetify.theme.themes.light.textColor = element.value;
                } else if (element.name === 'custom_theme_btn_primary_color' && element.value !== null) {
                    this.$vuetify.theme.themes.light.btnColorPrimary = element.value;
                } else if (element.name === 'custom_theme_btn_secondary_color' && element.value !== null) {
                    this.$vuetify.theme.themes.light.btnSecondary = element.value;
                } else if (element.name === 'custom_theme_btn_text_color' && element.value !== null) {
                    this.$vuetify.theme.themes.light.btnTextColor = element.value;
                } else if (element.name === 'custom_theme_header_footer_color' && element.value !== null) {
                    this.$vuetify.theme.themes.light.headerFooterColor = element.value;
                } else if (element.name === 'custom_theme_footer_icon_color' && element.value !== null) {
                    this.$vuetify.theme.themes.light.footerIconColor = element.value;
                } else if (element.name === 'custom_theme_footer_icon_selected_color' && element.value !== null) {
                    this.$vuetify.theme.themes.light.footerIconSelectedColor = element.value;
                }
            });
            // console.log('setThemeColorValues_AFTER', this.$vuetify.theme.themes.light);
        },
        setThemeDefaultColorValues() {
            this.$vuetify.theme.themes.light.backgroundColor = CONFIG.THEME_DEFAULT_COLORS.backgroundColor;
            this.$vuetify.theme.themes.light.textColor = CONFIG.THEME_DEFAULT_COLORS.textColor;
            this.$vuetify.theme.themes.light.btnColorPrimary = CONFIG.THEME_DEFAULT_COLORS.btnColorPrimary;
            this.$vuetify.theme.themes.light.btnSecondary = CONFIG.THEME_DEFAULT_COLORS.btnSecondary;
            this.$vuetify.theme.themes.light.btnTextColor = CONFIG.THEME_DEFAULT_COLORS.btnTextColor;
            this.$vuetify.theme.themes.light.headerFooterColor = CONFIG.THEME_DEFAULT_COLORS.headerFooterColor;
            this.$vuetify.theme.themes.light.footerIconColor = CONFIG.THEME_DEFAULT_COLORS.footerIconColor;
            this.$vuetify.theme.themes.light.footerIconSelectedColor = CONFIG.THEME_DEFAULT_COLORS.footerIconSelectedColor;
            this.$vuetify.theme.themes.light.imageFullscreenBackground = CONFIG.THEME_DEFAULT_COLORS.imageFullscreenBackground;
        },
        setEventLoadingDelayed() {
            if (this.event_loading_delayed_pointer) {
                clearTimeout(this.event_loading_delayed_pointer);
            }

            if (this.event_loading) {
                this.event_loading_delayed = true;
            } else {
                this.event_loading_delayed_pointer = setTimeout(() => {
                    this.event_loading_delayed = false;
                }, 500);
            }
        },

        setEventLoading(isLoading) {
            this.$store.commit('events/SET_EVENT_LOADING', isLoading);
        },
        addEventObject(event) {
            this.$store.commit('events/ADD_EVENT', event);
        },
        setSelectedEventInactive() {
            this.updateSelectedEventObject({ active: false });
        },
        setSelectedEventActive() {
            this.updateSelectedEventObject({ active: true });
        },
        updateSelectedEventObject(data) {
            let updateModel = this._.merge(this.selectedEvent, data);
            this.$store.commit('events/SET_SELECTED_EVENT', updateModel);
        },
        // options: gametype array or {gametypes, force}
        getEvents(options) {
            return this.$store.dispatch('events/getEvents', options);
        },
        getEventById(id) {
            return this.$store.dispatch('events/getEventById', id);
        },
        checkinEventById(id) {
            return this.$store.dispatch('events/checkinEventById', id);
        },
        readEventWithQR(qrData) {
            return new Promise((resolve, reject) => {
                this.$store
                    .dispatch('events/readNewEvent', qrData)
                    .then(eventId => {
                        if (eventId) {
                            resolve(eventId);
                        } else {
                            reject(null);
                        }
                    })
                    .catch(ex => {
                        reject(ex);
                    });
            });
        },
        readNewPin(pinData) {
            return new Promise((resolve, reject) => {
                this.$store
                    .dispatch('events/readNewPin', pinData)
                    .then(qrData => {
                        resolve(qrData);
                    })
                    .catch(ex => {
                        reject(ex);
                    });
            });
        },
        eventEntryHasChanged(newObj, oldObj) {
            return !Vue._.isEqual(newObj, oldObj);
        },
        // naive server date time ("2026-09-05 10:00:00") in the event timezone -> ms since epoch, NaN if unparsable
        parseInEventTimezone(naive, timezone) {
            const formats = ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm', moment.ISO_8601];
            const parsed = timezone ? moment.tz(String(naive), formats, timezone) : moment(String(naive), formats);
            return parsed.isValid() ? parsed.valueOf() : NaN;
        },
        getBackgroundImageUrl(name) {
            return this.imageBaseUrl + `/teamgeist/picture/event/${name}`;
        },
        checkinEvent(event_id) {
            return new Promise((resolve, reject) => {
                this.checkinError = false;
                this.checkinEventById(event_id)
                    .then(() => {
                        // console.log("checkinEvent()");
                        resolve(event_id);
                    })
                    .catch(() => {
                        reject();
                        this.checkinError = true;
                    });
            });
        },
        getPlayerinfo() {
            return this.$store.dispatch('events/getPlayerinfo', {});
        },
        getTeamPhoto() {
            return this.$store.dispatch('events/getTeamPhoto', {});
        },
        updateTeamPhoto(data) {
            this.$store.commit('events/SET_APP_TEAM_PHOTO', data);
        },

        updateTeamName(data) {
            this.$store.commit('events/SET_APP_TEAM_NAME', data);
        },

        setTeamName(data) {
            return this.$store.dispatch('events/setTeamName', data);
        },
        setParticipantNames(data) {
            return this.$store.dispatch('events/setParticipantNames', data);
        },

        destroyEvent() {
            return this.$store.dispatch('events/destroyEvent');
        }
    }
};
